import "./styles.scss";

import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Text, Button, Icon } from "@fragments";
import { UserAvatar } from "@components/Community";
import {
  IconArrowFork,
  IconArrowRight,
  IconPercentage,
  IconUsers,
  IconMail,
  IconPhoto,
  IconAlertTriangle,
  IconLinkOff,
  IconCircleCheck,
  IconCircleX,
  IconWallet,
} from "@tabler/icons-react";

import { SPLIT_INVITE_STATE } from "../enums/splitInviteStates";

/**
 * Private split invite (§2). A centered, elevated card rendered INSIDE the app
 * shell (the user is in-app, like /banking onboarding) — no brand InviteShell.
 * Shows the community, the offered percentile and a gated accept/decline area,
 * plus self-contained status screens for terminal / wrong-account outcomes.
 */
export default function SplitInviteView(props) {
  const { state } = props;

  let content;
  if (state === SPLIT_INVITE_STATE.LOADING) content = <InviteSkeleton />;
  else if (state === SPLIT_INVITE_STATE.VALID) content = <InviteCard {...props} />;
  else if (state === SPLIT_INVITE_STATE.WRONG_EMAIL) content = <WrongEmail {...props} />;
  else content = <InviteStatus state={state} />;

  return (
    <div className="split-invite">
      <div className="si-main">{content}</div>
    </div>
  );
}

// ── The live invite card (VALID) ───────────────────────────────────────────────
function InviteCard({
  community,
  owner,
  invitedEmail,
  percentile,
  awaitingWallet,
  submitting,
  canRespond,
  accept,
  decline,
}) {
  const { t } = useTranslation("member");
  const membersCount = community?.members_count;

  return (
    <article className="si-card">
      {/* Banner */}
      <div className="si-banner" style={{ "--i": 0 }}>
        {community?.banner ? (
          <img src={community.banner} alt={community?.name || t("common:entities.community")} loading="lazy" />
        ) : (
          <div className="si-banner-fallback">
            <IconPhoto size={30} />
          </div>
        )}
      </div>

      <div className="si-body">
        <span className="si-eyebrow" style={{ "--i": 1 }}>
          <Text.span contrast={6} left={<IconArrowFork size={14} />}>
            {t("splitInvite.eyebrow")}
          </Text.span>
        </span>

        <div className="si-title" style={{ "--i": 2 }}>
          <Text.h2 contrast={9} weight={500}>
            {community?.name || t("common:entities.community")}
          </Text.h2>
        </div>

        {/* Community meta — members + owner */}
        <div className="si-meta" style={{ "--i": 3 }}>
          {membersCount != null && (
            <Text.span contrast={5} left={<IconUsers size={15} />}>
              {t("splitInvite.members", { count: Number(membersCount) })}
            </Text.span>
          )}
          {owner?.name && (
            <div className="si-owner">
              <UserAvatar
                user={{ username: owner.name, avatar: owner.avatar }}
                size={24}
              />
              <Text.span contrast={6}>{owner.name}</Text.span>
            </div>
          )}
        </div>

        {/* Offered share */}
        <div className="si-terms" style={{ "--i": 4 }}>
          <div className="si-term">
            <Text.span contrast={5} left={<IconPercentage size={14} />}>
              {t("splitInvite.offeredShare")}
            </Text.span>
            <Text.span contrast={9} weight={500}>
              {percentile != null ? `${percentile}%` : "—"}
            </Text.span>
          </div>
        </div>

        {/* Who it's for */}
        <div className="si-invited" style={{ "--i": 5 }}>
          <span className="si-invited-icon">
            <IconMail size={15} />
          </span>
          <Text.span contrast={5}>
            <Trans
              t={t}
              i18nKey="splitInvite.invitedTo"
              values={{ email: invitedEmail }}
              components={{ strong: <strong /> }}
            />
          </Text.span>
        </div>

        {awaitingWallet && (
          <div className="si-wallet-hint" style={{ "--i": 6 }}>
            <span className="si-wallet-icon">
              <IconWallet size={16} />
            </span>
            <Text.span contrast={6}>
              {t("splitInvite.walletHint")}
            </Text.span>
          </div>
        )}

        <div className="si-divider" />

        {/* Gated actions */}
        <div className="si-actions" style={{ "--i": 7 }}>
          <Button
            text={t("splitInvite.accept")}
            color="primary"
            size="large"
            endIcon={<IconArrowRight size={18} />}
            onClick={() => accept()}
            disabled={!canRespond || submitting}
            fullWidth
          />
          <Button
            text={t("splitInvite.decline")}
            color="overlay"
            variant="rounded"
            size="large"
            onClick={decline}
            disabled={!canRespond || submitting}
            preventLoading
            fullWidth
          />
        </div>
      </div>
    </article>
  );
}

// ── Wrong-account screen (signed in as someone else) ───────────────────────────
function WrongEmail({ accountEmail, switchAccount }) {
  const { t } = useTranslation("member");

  return (
    <article className="si-card si-card--status">
      <div className="si-status">
        <Icon.Sphere size={76}>
          <IconAlertTriangle />
        </Icon.Sphere>
        <Text.h3 contrast={8} weight={500}>
          {t("splitInvite.wrongEmail.title")}
        </Text.h3>
        <Text.span contrast={5} className="si-status-sub">
          <Trans
            t={t}
            i18nKey="splitInvite.wrongEmail.description"
            values={{ email: accountEmail }}
            components={{ strong: <strong /> }}
          />
        </Text.span>
        <Button
          text={t("splitInvite.wrongEmail.switchAccount")}
          color="inverse"
          variant="rounded"
          onClick={switchAccount}
          preventLoading
        />
      </div>
    </article>
  );
}

// ── Terminal / status screens ───────────────────────────────────────────────────
// Copy is resolved at render so it follows the active language; the state keys
// come from SPLIT_INVITE_STATE and are never translated.
const getStatusCopy = (t) => ({
  [SPLIT_INVITE_STATE.ACCEPTED]: {
    Icon: IconCircleCheck,
    title: t("splitInvite.status.accepted.title"),
    subtitle: t("splitInvite.status.accepted.subtitle"),
    cta: { label: t("splitInvite.status.accepted.cta"), href: "/banking" },
  },
  [SPLIT_INVITE_STATE.NOT_FOUND]: {
    Icon: IconLinkOff,
    title: t("splitInvite.status.notFound.title"),
    subtitle: t("splitInvite.status.notFound.subtitle"),
    cta: { label: t("splitInvite.status.notFound.cta"), href: "/" },
  },
  [SPLIT_INVITE_STATE.EXPIRED]: {
    Icon: IconAlertTriangle,
    title: t("splitInvite.status.expired.title"),
    subtitle: t("splitInvite.status.expired.subtitle"),
    cta: { label: t("splitInvite.status.expired.cta"), href: "/" },
  },
  [SPLIT_INVITE_STATE.USED]: {
    Icon: IconCircleCheck,
    title: t("splitInvite.status.used.title"),
    subtitle: t("splitInvite.status.used.subtitle"),
    cta: { label: t("splitInvite.status.used.cta"), href: "/banking" },
  },
  [SPLIT_INVITE_STATE.DECLINED]: {
    Icon: IconCircleX,
    title: t("splitInvite.status.declined.title"),
    subtitle: t("splitInvite.status.declined.subtitle"),
    cta: { label: t("splitInvite.status.declined.cta"), href: "/" },
  },
  [SPLIT_INVITE_STATE.ERROR]: {
    Icon: IconAlertTriangle,
    title: t("splitInvite.status.error.title"),
    subtitle: t("splitInvite.status.error.subtitle"),
    cta: { label: t("splitInvite.status.error.cta"), reload: true },
  },
});

function InviteStatus({ state }) {
  const { t } = useTranslation("member");
  const statusCopy = useMemo(() => getStatusCopy(t), [t]);

  const copy = statusCopy[state] ?? statusCopy[SPLIT_INVITE_STATE.ERROR];
  const { Icon: StatusIcon, title, subtitle, cta } = copy;

  function onCta() {
    if (cta?.reload) window.location.reload();
    else if (cta?.href) window.location.href = cta.href;
  }

  return (
    <article className="si-card si-card--status">
      <div className="si-status">
        <Icon.Sphere size={76}>
          <StatusIcon />
        </Icon.Sphere>
        <Text.h3 contrast={8} weight={500}>
          {title}
        </Text.h3>
        <Text.span contrast={5} className="si-status-sub">
          {subtitle}
        </Text.span>
        {cta && (
          <Button
            text={cta.label}
            color="inverse"
            variant="rounded"
            onClick={onCta}
            preventLoading
          />
        )}
      </div>
    </article>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function InviteSkeleton() {
  return (
    <article className="si-card is-skeleton" aria-busy="true">
      <div className="si-banner skel" />
      <div className="si-body">
        <div className="skel skel-line" style={{ width: "40%", height: 20 }} />
        <div className="skel skel-line" style={{ width: "70%", height: 28 }} />
        <div className="skel skel-line" style={{ width: "55%", height: 18 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 56 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 48 }} />
      </div>
    </article>
  );
}
